/** Pass 2d: Resolve ES module import specifiers to file→file graph edges. */
import fs from 'node:fs';
import path from 'node:path';
import { logger } from '../../logger.js';
import type { ChangeScope } from '../../plugin-api/types.js';
import type { PipelineState } from '../pipeline-state.js';
import { IndexAbortedError } from '../index-abort.js';
import { EsModuleResolver } from '../resolvers/es-modules.js';
import { ESM_IMPORT_LANGUAGES } from './import-capable-languages.js';
import { PhantomPackageFactory } from './phantom-externals.js';
import { commitInChunks, RESOLVER_WRITE_CHUNK } from '../resolver-budget.js';

/**
 * Derive a package-bucket name from an npm specifier. Scoped packages
 * (`@vue/reactivity/...`) collapse to `@vue/reactivity`; plain packages
 * (`lodash/fp/pipe`) collapse to `lodash`.
 */
function npmBucketFor(specifier: string): string | null {
  if (!specifier) return null;
  if (specifier.startsWith('.') || specifier.startsWith('/')) return null;
  if (specifier.startsWith('@/') || specifier.startsWith('~') || specifier.startsWith('#'))
    return null;
  if (specifier.startsWith('node:')) return specifier.split('/')[0];
  if (specifier.startsWith('@')) {
    const parts = specifier.split('/');
    if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
    return parts[0];
  }
  return specifier.split('/')[0];
}

export async function resolveEsmImportEdges(
  state: PipelineState,
  _scope?: ChangeScope,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) throw new IndexAbortedError('resolveEsmImportEdges aborted');
  const { store } = state;
  if (!store.db.open) throw new IndexAbortedError('Database is closed');
  // WHY: ESM imports are driven entirely by `state.pendingImports`, which is
  // already populated only for re-extracted files. Scope is accepted for API
  // symmetry but adds nothing.
  void _scope;
  if (state.pendingImports.size === 0) return;

  let resolver: EsModuleResolver;
  try {
    const workspacePaths = state.workspaces?.map((ws) => ws.path) ?? [];
    resolver = new EsModuleResolver(state.rootPath, workspacePaths);
  } catch {
    logger.warn('EsModuleResolver init failed — skipping import edge resolution');
    return;
  }

  let created = 0;
  let phantomEdges = 0;
  const phantomPackages = new PhantomPackageFactory(state, 'typescript');
  const phantomEdgesSeen = new Set<string>();

  const pendingFileIds = Array.from(state.pendingImports.keys());
  const fileMap = store.getFilesByIds(pendingFileIds);
  const fileNodeMap = store.getNodeIdsBatch('file', pendingFileIds);

  // Workspace lookup for pending source files (needed to scope phantom packages)
  const fileWorkspace = new Map<number, string | null>();
  {
    const ids = pendingFileIds;
    const CHUNK = 500;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      const ph = chunk.map(() => '?').join(',');
      const rows = store.db
        .prepare(`SELECT id, workspace FROM files WHERE id IN (${ph})`)
        .all(...chunk) as Array<{ id: number; workspace: string | null }>;
      for (const r of rows) fileWorkspace.set(r.id, r.workspace);
    }
  }

  const targetFileCache = new Map<string, { id: number; nodeId: number } | null>();
  const resolveTargetFile = (relPath: string): { id: number; nodeId: number } | null => {
    // Files are stored with forward-slash paths (fast-glob normalizes on Windows
    // too), but path.relative on Windows returns "src\foo.ts" — normalize before
    // lookup so the cache key and store query match what's in the files table.
    const key = path.sep === '/' ? relPath : relPath.split(path.sep).join('/');
    const cached = targetFileCache.get(key);
    if (cached !== undefined) return cached;
    const f = store.getFile(key);
    if (!f) {
      targetFileCache.set(key, null);
      return null;
    }
    const nodeId = store.getNodeId('file', f.id);
    if (nodeId == null) {
      targetFileCache.set(key, null);
      return null;
    }
    const entry = { id: f.id, nodeId };
    targetFileCache.set(key, entry);
    return entry;
  };

  const importsEdgeType = store.db
    .prepare('SELECT id FROM edge_types WHERE name = ?')
    .get('imports') as { id: number } | undefined;
  if (!importsEdgeType) return;

  // oxc-resolver realpaths symlinks in its output (/var → /private/var,
  // /tmp → /private/tmp on macOS) while rootPath may keep the unresolved
  // spelling (os.tmpdir() differs per machine/env). Comparing the raw pair
  // then yields `..`-paths for files that ARE in the project, and every
  // relative import is silently skipped (TRA-1017: an entire CI platform
  // lost all ESM edges this way with zero errors logged). Resolve the root
  // once per pass (best-effort — a vanished root falls back to the raw
  // string, i.e. today's behaviour) and accept whichever base keeps the
  // target in-root, so resolved and unresolved spellings both work.
  let realRoot: string;
  try {
    realRoot = fs.realpathSync(state.rootPath);
  } catch {
    realRoot = state.rootPath;
  }
  const toInRootRel = (abs: string): string | null => {
    for (const base of [realRoot, state.rootPath]) {
      const rel = path.relative(base, abs);
      if (rel !== '' && !rel.startsWith('..') && !path.isAbsolute(rel)) return rel;
    }
    return null;
  };

  const insertStmt = store.db.prepare(
    `INSERT INTO edges (source_node_id, target_node_id, edge_type_id, resolved, metadata, is_cross_ws)
     VALUES (?, ?, ?, 1, ?, 0)
     ON CONFLICT(source_node_id, target_node_id, edge_type_id)
     DO UPDATE SET metadata = excluded.metadata`,
  );

  // TRA-1764: one transaction per chunk with a fair yield between chunks —
  // bulk watcher batches (2713+ files) used to resolve 6k+ import edges in a
  // single synchronous transaction, starving /health on the same thread.
  const pendingEntries = Array.from(state.pendingImports);
  await commitInChunks(
    store.db,
    pendingEntries,
    (chunk) => {
      for (const [fileId, imports] of chunk) {
        const file = fileMap.get(fileId);
        if (!file) continue;
        // Skip non-JS/TS files — PHP/Python imports are handled by their own
        // resolvers. Without this guard, PHP `use` entries (PHP FQNs like
        // `App\Actions\Foo`) get run through npm bucketing and pollute the
        // phantom graph.
        if (!ESM_IMPORT_LANGUAGES.has(file.language ?? '')) continue;

        const absSource = path.resolve(state.rootPath, file.path);
        const sourceNodeId = fileNodeMap.get(fileId);
        if (sourceNodeId == null) continue;

        const consolidated = new Map<string, string[]>();
        for (const { from, specifiers } of imports) {
          const existing = consolidated.get(from);
          if (existing) {
            existing.push(...specifiers);
          } else {
            consolidated.set(from, [...specifiers]);
          }
        }

        const sourceWs = fileWorkspace.get(fileId) ?? null;

        for (const [from, specifiers] of consolidated) {
          if (!from) continue; // External URL (HTML/CSS href to CDN) — not a file nor an npm package.
          if (
            from.startsWith('http://') ||
            from.startsWith('https://') ||
            from.startsWith('//') ||
            from.startsWith('data:')
          )
            continue;
          const isRelative =
            from.startsWith('.') ||
            from.startsWith('/') ||
            from.startsWith('@/') ||
            from.startsWith('~');

          // Always try the path resolver first. Bare specifiers may still be
          // local — tsconfig/jsconfig `baseUrl` or `paths` can turn something
          // like `static/svg/Logo.svg` into a file inside the repo. Only fall
          // back to npm phantom when resolution lands outside the project root.
          const resolved = resolver.resolve(from, absSource);
          if (resolved) {
            const relTarget = toInRootRel(resolved);
            // `path.relative` returns something starting with `..` when the
            // target is outside rootPath — those are real npm deps under
            // node_modules, handled by the bucket fallback below.
            if (relTarget !== null) {
              const target = resolveTargetFile(relTarget);
              if (target) {
                insertStmt.run(
                  sourceNodeId,
                  target.nodeId,
                  importsEdgeType.id,
                  JSON.stringify({ from, specifiers }),
                );
                created++;
                continue;
              }
            }
          }

          if (isRelative) {
            // Unresolved relative import — likely points outside the indexed
            // source tree or into a generated file. Skip rather than phantom
            // (would pollute the bucket view with per-file noise).
            continue;
          }

          // Bare specifier — npm package, node: builtin, etc. Anchor to a
          // phantom package bucket so consumers of the same library cluster.
          const bucket = npmBucketFor(from);
          if (!bucket) continue;
          const dedupKey = `${sourceNodeId}\0${sourceWs ?? ''}\0${bucket}`;
          if (phantomEdgesSeen.has(dedupKey)) continue;
          phantomEdgesSeen.add(dedupKey);

          const pkg = phantomPackages.ensure(bucket, sourceWs);
          if (pkg.node_id === sourceNodeId) continue;
          insertStmt.run(
            sourceNodeId,
            pkg.node_id,
            importsEdgeType.id,
            JSON.stringify({ from, specifiers, external: true, bucket }),
          );
          phantomEdges++;
        }
      }
    },
    RESOLVER_WRITE_CHUNK,
    signal,
  );

  if (created > 0 || phantomEdges > 0) {
    logger.info({ edges: created, phantomEdges }, 'ES module import edges resolved');
  }
}
